import Clinic from "@/pages/Clinic";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import SplashIntro from "@/components/SplashIntro";
import { KioskGuard, UpdateBanner } from "@/components/KioskGuard";
import { RemoteMessage } from "@/components/RemoteMessage";
import { AppProvider, useApp } from "@/context/AppContext";
import AppShell from "@/components/AppShell";
import FridayLock from "@/components/FridayLock";
import { BankAlerts } from "@/components/bank/BankAlerts";
import { BillingAlerts } from "@/components/billing/BillingAlerts";
import { WholesaleAlerts } from "@/components/wholesale/WholesaleAlerts";
import PinLock from "@/components/PinLock";
import { AccountGate } from "@/components/AccountGate";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Sell from "@/pages/Sell";
import Customers from "@/pages/Customers";
import Patients from "@/pages/Patients";
import Vault from "@/pages/Vault";
import Bank from "@/pages/Bank";
import Finance from "@/pages/Finance";
import Exchange from "@/pages/Exchange";
import Landing from "@/pages/Landing";
import Book from "@/pages/Book";
import { clinicScope, scopeAllows } from "@/lib/scope";
const CustomersOrPatients = () => { const { view } = useApp(); return view.shop?.type === "clinic" ? <Patients /> : <Customers />; };
const HomeRedirect = () => { const { view } = useApp(); return <Navigate to={clinicScope(view.shop) ? "/clinic" : view.shop?.type === "exchange" ? "/exchange" : "/sell"} replace />; };
import Inventory from "@/pages/Inventory";
import Purchases from "@/pages/Purchases";
import Marketing from "@/pages/Marketing";
import Reports from "@/pages/Reports";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import More from "@/pages/More";
import Admin from "@/pages/Admin";
import SalesHistory from "@/pages/SalesHistory";
import Expenses from "@/pages/Expenses";
import Orders from "@/pages/Orders";
import Tpv from "@/pages/Tpv";
import PriceCheck from "@/pages/PriceCheck";
import Terminal from "@/pages/Terminal";
import Billing from "@/pages/Billing";
import CustomerDisplay from "@/pages/CustomerDisplay";
import PublicReceipt from "@/pages/PublicReceipt";
import CustomOrders from "@/pages/CustomOrders";
import Visualizer from "@/pages/Visualizer";
import Charity from "@/pages/Charity";
import DebtAging from "@/pages/DebtAging";
import Waste from "@/pages/Waste";
import Cameras from "@/pages/Cameras";
import PhoneCam from "@/pages/PhoneCam";
import Wholesale from "@/pages/Wholesale";
import SupplierHub from "@/pages/SupplierHub";

function Guard({ perm, children }) {
  const { can, view } = useApp();
  const scoped = clinicScope(view.shop);
  if (scoped && !scopeAllows(view.shop, window.location.pathname)) return <Navigate to="/clinic" replace />;
  const fx = view.shop?.type === "exchange";
  if (fx && !["/exchange", "/expenses", "/vault", "/bank", "/finance", "/reports", "/team", "/settings", "/billing", "/more", "/tpv", "/cameras", "/wholesale"].some((p) => window.location.pathname.startsWith(p))) return <Navigate to="/exchange" replace />;
  return can(perm) ? children : <Navigate to={scoped ? "/clinic" : fx ? "/exchange" : "/sell"} replace />;
}

function AppRoutes() {
  const { user, loaded, staff, t } = useApp();
  const { pathname } = useLocation();
  if (pathname === "/display") return <CustomerDisplay />;
  if (pathname === "/r") return <PublicReceipt />;
  if (pathname === "/cam-phone") return <PhoneCam />;
  if (pathname === "/book") return <Book />;
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/book" element={<Book />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  if (user.role === "superadmin" || user.role === "platform_admin") {
    return (
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }
  if (!loaded) return <AccountGate><div className="min-h-screen grid place-items-center font-bold" data-testid="app-loading">{t("loading")}</div></AccountGate>;
  if (!staff) return <AccountGate><PinLock /></AccountGate>;
  return (
    <AccountGate>
    <FridayLock />
    <BankAlerts />
    <BillingAlerts />
    <WholesaleAlerts />
    <KioskGuard />
    <Routes>
      <Route path="/price-check" element={<PriceCheck />} />
      <Route path="/terminal" element={<Terminal />} />
      <Route element={<AppShell />}>
        <Route path="/sell" element={<Guard><Sell /></Guard>} />
        <Route path="/clinic" element={<Guard perm="clinic"><Clinic /></Guard>} />
        <Route path="/customers" element={<Guard perm="customers"><CustomersOrPatients /></Guard>} />
        <Route path="/inventory" element={<Guard perm="inventory"><Inventory /></Guard>} />
        <Route path="/purchases" element={<Guard perm="purchases"><Purchases /></Guard>} />
        <Route path="/sales" element={<Guard perm="sell"><SalesHistory /></Guard>} />
        <Route path="/expenses" element={<Guard perm="purchases"><Expenses /></Guard>} />
        <Route path="/orders" element={<Guard perm="purchases"><Orders /></Guard>} />
        <Route path="/custom-orders" element={<Guard perm="sell"><CustomOrders /></Guard>} />
        <Route path="/visualizer" element={<Guard perm="sell"><Visualizer /></Guard>} />
        <Route path="/charity" element={<Guard perm="sell"><Charity /></Guard>} />
        <Route path="/debt-aging" element={<Guard perm="customers"><DebtAging /></Guard>} />
        <Route path="/waste" element={<Guard perm="inventory"><Waste /></Guard>} />
        <Route path="/cameras" element={<Guard perm="cameras"><Cameras /></Guard>} />
        <Route path="/wholesale" element={<Guard perm="purchases"><Wholesale /></Guard>} />
        <Route path="/supplier-hub" element={<Guard perm="purchases"><SupplierHub /></Guard>} />
        <Route path="/marketing" element={<Guard perm="marketing"><Marketing /></Guard>} />
        <Route path="/reports" element={<Guard perm="reports"><Reports /></Guard>} />
        <Route path="/team" element={<Guard perm="team"><Team /></Guard>} />
        <Route path="/billing" element={<Guard perm="billing"><Billing /></Guard>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/bank" element={<Guard perm="bank"><Bank /></Guard>} />
        <Route path="/finance" element={<Guard><Finance /></Guard>} />
        <Route path="/exchange" element={<Guard perm="sell"><Exchange /></Guard>} />
        <Route path="/tpv" element={<Guard><Tpv /></Guard>} />
        <Route path="/more" element={<More />} />
        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
    </AccountGate>
  );
}

export default function App() {
  return (
    <AppProvider>
      <SplashIntro>
        <BrowserRouter>
          <AppRoutes />
          <UpdateBanner />
          <RemoteMessage />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </SplashIntro>
    </AppProvider>
  );
}
